import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { FileText, Plus, Trash2, Edit2, Send, Receipt, Loader2, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-muted text-muted-foreground', sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  received: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

const BillingPage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'hr'].includes(user?.role);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('client');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    invoice_type: 'client', client_name: '', client_email: '', client_address: '', client_gst: '',
    items: [{ description: '', quantity: 1, amount: '' }],
    gst_rate: 18, payment_terms: 'Net 30', due_date: '', notes: '', status: 'draft',
  });

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices?invoice_type=${activeTab}`, { withCredentials: true });
      setInvoices(res.data);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, amount: '' }] });
  const removeItem = (idx) => { const items = [...form.items]; items.splice(idx, 1); setForm({ ...form, items }); };
  const updateItem = (idx, field, val) => { const items = [...form.items]; items[idx][field] = val; setForm({ ...form, items }); };

  const subtotal = form.items.reduce((s, i) => s + (parseFloat(i.amount) || 0) * (parseFloat(i.quantity) || 1), 0);
  const gstAmount = Math.round(subtotal * (parseFloat(form.gst_rate) || 0) / 100);
  const total = subtotal + gstAmount;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_name) { toast.error('Client/Vendor name is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/invoices`, {
        ...form, invoice_type: activeTab,
        items: form.items.map(i => ({ ...i, amount: parseFloat(i.amount) || 0, quantity: parseFloat(i.quantity) || 1 })),
        gst_rate: parseFloat(form.gst_rate) || 18,
      }, { withCredentials: true });
      toast.success(`${activeTab === 'client' ? 'Invoice' : 'Bill'} created`);
      setIsCreateOpen(false);
      setForm({ invoice_type: activeTab, client_name: '', client_email: '', client_address: '', client_gst: '', items: [{ description: '', quantity: 1, amount: '' }], gst_rate: 18, payment_terms: 'Net 30', due_date: '', notes: '', status: 'draft' });
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (invoiceId, status) => {
    try {
      await axios.put(`${API_URL}/api/invoices/${invoiceId}`, { status }, { withCredentials: true });
      toast.success('Status updated');
      fetchInvoices();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${API_URL}/api/invoices/${invoiceId}`, { withCredentials: true });
      toast.success('Deleted');
      fetchInvoices();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const formatCurrency = (amt) => `INR ${(amt || 0).toLocaleString('en-IN')}`;
  const clientInvoices = invoices.filter(i => i.invoice_type === 'client');
  const vendorBills = invoices.filter(i => i.invoice_type === 'vendor');
  const currentList = activeTab === 'client' ? clientInvoices : vendorBills;
  const totalRevenue = clientInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
  const totalPayable = vendorBills.filter(i => i.status === 'pending').reduce((s, i) => s + (i.total || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  if (!isAdmin) {
    return <div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Billing module is restricted to Admin/HR only.</p></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="billing-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><IndianRupee className="w-7 h-7 text-primary" />Billing & Invoicing</h1>
          <p className="text-muted-foreground">Manage client invoices and vendor bills</p>
        </div>
        <Button onClick={() => { setForm({ ...form, invoice_type: activeTab }); setIsCreateOpen(true); }} className="gap-1.5" data-testid="create-invoice-btn">
          <Plus className="w-4 h-4" />{activeTab === 'client' ? 'New Invoice' : 'New Bill'}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total Invoices</p><p className="text-2xl font-bold">{clientInvoices.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Revenue (Paid)</p><p className="text-2xl font-bold text-[hsl(var(--success))]">{formatCurrency(totalRevenue)}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Vendor Bills</p><p className="text-2xl font-bold">{vendorBills.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Payable (Pending)</p><p className="text-2xl font-bold text-amber-500">{formatCurrency(totalPayable)}</p></CardContent></Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="client" data-testid="tab-client"><FileText className="w-4 h-4 mr-1.5" />Client Invoices</TabsTrigger>
          <TabsTrigger value="vendor" data-testid="tab-vendor"><Receipt className="w-4 h-4 mr-1.5" />Vendor Bills</TabsTrigger>
        </TabsList>
        <TabsContent value={activeTab} className="mt-4">
          {currentList.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No {activeTab === 'client' ? 'invoices' : 'bills'} yet</CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr>
                      <th>#</th><th>{activeTab === 'client' ? 'Client' : 'Vendor'}</th><th>Items</th><th>Subtotal</th><th>GST</th><th>Total</th><th>Due Date</th><th>Status</th><th>Actions</th>
                    </tr></thead>
                    <tbody>
                      {currentList.map(inv => (
                        <tr key={inv.id}>
                          <td className="font-mono text-xs">{inv.invoice_number}</td>
                          <td className="font-medium text-sm">{inv.client_name}</td>
                          <td className="text-xs">{(inv.items || []).length} items</td>
                          <td className="font-mono text-xs">{formatCurrency(inv.subtotal)}</td>
                          <td className="font-mono text-xs">{formatCurrency(inv.gst_amount)} ({inv.gst_rate}%)</td>
                          <td className="font-mono font-bold">{formatCurrency(inv.total)}</td>
                          <td className="text-xs">{inv.due_date || '—'}</td>
                          <td>
                            <Select value={inv.status} onValueChange={(v) => handleStatusChange(inv.id, v)}>
                              <SelectTrigger className="h-7 text-xs w-24 p-1"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="sent">Sent</SelectItem>
                                <SelectItem value={activeTab === 'client' ? 'paid' : 'received'}>{activeTab === 'client' ? 'Paid' : 'Received'}</SelectItem>
                                <SelectItem value={activeTab === 'client' ? 'overdue' : 'pending'}>{activeTab === 'client' ? 'Overdue' : 'Pending'}</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(inv.id)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Invoice/Bill Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{activeTab === 'client' ? 'New Client Invoice' : 'New Vendor Bill'}</DialogTitle>
            <DialogDescription>Fill in the details below. GST is auto-calculated.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{activeTab === 'client' ? 'Client' : 'Vendor'} Name *</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required data-testid="inv-client-name" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>GST Number</Label><Input value={form.client_gst} onChange={(e) => setForm({ ...form, client_gst: e.target.value })} placeholder="GSTIN" /></div>
              <div className="space-y-2"><Label>Address</Label><Input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} /></div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Line Items</Label><Button type="button" size="sm" variant="outline" onClick={addItem} className="gap-1"><Plus className="w-3 h-3" />Add Item</Button></div>
              {form.items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-[2fr_80px_120px_auto] gap-2 items-center">
                  <Input value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="Description" className="text-sm" />
                  <Input type="number" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} placeholder="Qty" className="text-sm" />
                  <Input type="number" value={item.amount} onChange={(e) => updateItem(idx, 'amount', e.target.value)} placeholder="Amount" className="text-sm" />
                  {form.items.length > 1 && <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeItem(idx)}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>GST Rate (%)</Label><Input type="number" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} data-testid="inv-gst-rate" /></div>
              <div className="space-y-2"><Label>Payment Terms</Label>
                <Select value={form.payment_terms} onValueChange={(v) => setForm({ ...form, payment_terms: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Net 15">Net 15</SelectItem><SelectItem value="Net 30">Net 30</SelectItem><SelectItem value="Net 45">Net 45</SelectItem><SelectItem value="Due on Receipt">Due on Receipt</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>

            <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Payment instructions, terms..." rows={2} /></div>

            <div className="bg-muted/50 p-4 rounded-lg space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal:</span><span className="font-mono">{formatCurrency(subtotal)}</span></div>
              <div className="flex justify-between"><span>GST ({form.gst_rate}%):</span><span className="font-mono">{formatCurrency(gstAmount)}</span></div>
              <div className="flex justify-between font-bold text-base border-t pt-2 mt-2"><span>Total:</span><span className="font-mono">{formatCurrency(total)}</span></div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="create-invoice-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {activeTab === 'client' ? 'Create Invoice' : 'Record Bill'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingPage;
